import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import type { ToolActionHandlers } from '@/components/agent/tools/_shared/chat-tool-actions'
import { ToolCardActions } from '@/components/agent/tools/_shared/ToolCardActions'
import { SnippetRows } from '@/components/source-snippets/SnippetRows'
import { useApplyContributionToGraph } from '@/features/agent/tools/useApplyContributionToGraph'
import { useToolCardActions } from '@/features/agent/tools/useToolCardActions'
import { useSelectionStore } from '@/stores/selection-store'
import type { SourceSnippet } from '@/types/source-snippet'
import { snippetNodeId, upsertSourceSnippet } from './object-set'
import { mappedEntityTypes } from './result'

export function SourceSnippetResult({ snippet }: { snippet: SourceSnippet }) {
  return (
    <div id={`source-result-${snippet.id}`} className="min-w-0 space-y-2">
      <p className="text-xs font-medium">{snippet.title}</p>
      <SnippetMapping snippet={snippet} />
      <SnippetRows snippet={snippet} />
      {snippet.snapshot.result.truncated && (
        <p className="text-xs text-muted-foreground">
          {snippet.snapshot.result.rows.length} of {snippet.snapshot.result.total_rows} rows shown.
          These rows will be added.
        </p>
      )}
      <SourceSnippetActions snippet={snippet} />
    </div>
  )
}

export function SnippetMapping({ snippet }: { snippet: SourceSnippet }) {
  const types = mappedEntityTypes(snippet)
  return (
    <p className="text-xs text-muted-foreground">
      {types.length ? `Mapped entity types: ${types.join(', ')}` : 'No mapped entity types'}
    </p>
  )
}

function SourceSnippetActions({ snippet }: { snippet: SourceSnippet }) {
  const apply = useApplyContributionToGraph(snippet.snapshot.caseId)
  const navigate = useNavigate()
  const id = snippetNodeId(snippet.id)
  const handlers = useMemo<ToolActionHandlers>(() => {
    const run = (mode: 'explore' | 'add') => {
      try {
        apply(mode, upsertSourceSnippet(null, snippet), snippet.title)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Could not add source rows.')
      }
    }
    return {
      showOnGraph: () => run('explore'),
      showSeparately: () => run('explore'),
      addToGraph: () => run('add'),
      highlight: () => {
        useSelectionStore.getState().focusEntity(id, 'graph')
        navigate(`/cases/${snippet.snapshot.caseId}/graph`)
      },
    }
  }, [apply, snippet, id, navigate])
  const model = useToolCardActions([id], handlers)
  return model ? <ToolCardActions model={model} handlers={handlers} /> : null
}
